/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState, useEffect } from 'react'
import moment from 'moment'
import {
    Table,
    Button,
    Menu,
    Dropdown,
    Space,
    Avatar,
    Spin,
    Form,
    Modal,
    Input,
    message,
    Checkbox,
    Switch,
} from 'antd'
import {
    MoreOutlined,
    CaretDownOutlined,
    LeftOutlined,
    RightOutlined,
    PlusOutlined,
} from '@ant-design/icons'
import { dataSource } from './dataSource'
import {
    Main,
    UserInfoCell,
    PaginationBar,
    Container,
} from './UsersTable.styles'
import { useAppSelector } from '../../../store/hooks'
import { setUserList } from '../../../store/slices/admin/admin'
import {
    fetchAllUsers,
    fetchSaveUserAccess,
    isLoading,
    fetchInviteUser,
    IInviteUser,
    getRegularRules,
    AdminDeleteUser,
    AdminUpdateUser,
} from '../../../utils'
import store from '../../../store'
import { SUBSCRIPTION, STORAGE_KEYS } from '../../../utils/consts'
import { REQUEST_STATUS } from '../../../utils/types'
import { UpdateUser } from './UsersTable.types'
import { fetchUser } from '../../../store/slices/user/user'

const pageOptions = [5, 10, 15, 20]

const Users: React.FC = () => {
    const { users } = useAppSelector((state) => state.admin!)
    const [requestStatus, setRequestStatus] = React.useState<REQUEST_STATUS>(
        REQUEST_STATUS.NONE
    )
    const [showingData, setShowingData] = React.useState<any[]>([])
    const [pageSize, setPageSize] = React.useState<number>(pageOptions[0])
    const [totalSize, setTotalSize] = React.useState<number>(dataSource.length)
    const [currentPage, setCurrentPage] = React.useState<number>(1)

    const [isModalVisible, setIsModalVisible] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isEditModalVisible, setIsEditModalVisible] = useState(false)

    const [editingUser, setEditingUser] = useState<any>(null)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()

    React.useEffect(() => {
        setCurrentPage(1)
    }, [totalSize, pageSize])
    useEffect(() => {
        if (!users.length) {
            fetchAllUsers()
        }
    }, [users])

    React.useEffect(() => {
        setShowingData(
            users.slice(
                (currentPage - 1) * pageSize,
                // added a fix additional bug :slice(0, 9) returns indices 0 to 8 (9 items),
                currentPage * pageSize
            )
        )
    }, [currentPage, pageSize, users])

    const getName = (user: any) => {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`
        }
        return user.fullName || ''
    }

    const handleDeleteUser = (user: any) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this user?',
            content: `This action cannot be undone. ${getName(
                user
            )} will be permanently removed.`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await AdminDeleteUser(user.id)
                    await fetchAllUsers()
                    message.success('User deleted successfully')
                } catch (error) {
                    message.error('Failed to delete user')
                }
            },
        })
    }

    // [NEW] Handle Edit Click
    const handleEditClick = (user: any) => {
        setEditingUser(user)
        editForm.setFieldsValue({
            firstName: user.firstName,
            lastName: user.lastName,
            isAdmin: user.isAdmin,
        })
        setIsEditModalVisible(true)
    }

    // [NEW] Handle Edit Submission
    const handleUpdateUser = async (values: UpdateUser) => {
        if (!editingUser) return
        setIsSubmitting(true)
        try {
            await AdminUpdateUser(editingUser.id, values)
            const currentUserId = Number(localStorage.getItem(STORAGE_KEYS.ID))
            if (editingUser.id === currentUserId) {
                store.dispatch(fetchUser()) // Refresh current user's full profile
            }
            await fetchAllUsers()
            message.success('User updated successfully')
            setIsEditModalVisible(false)
            setEditingUser(null)
            editForm.resetFields()
        } catch (error) {
            console.error(error)
            message.error('Failed to update user')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleUpdateAccess = useCallback((user: any, status: boolean) => {
        setRequestStatus(REQUEST_STATUS.PENDING)
        fetchSaveUserAccess(user.id, status)
            .then(() => {
                setRequestStatus(REQUEST_STATUS.SUCCESS)
            })
            .catch(() => {
                setRequestStatus(REQUEST_STATUS.FAILED)
            })
    }, [])

    const actionMenu = (user: any) => {
        return (
            <Menu>
                <Menu.Item key="edit" onClick={() => handleEditClick(user)}>
                    Edit
                </Menu.Item>
                <Menu.Item
                    disabled={!user.disabled}
                    onClick={() => handleUpdateAccess(user, false)}
                >
                    Enable
                </Menu.Item>
                <Menu.Item
                    disabled={user.disabled}
                    onClick={() => handleUpdateAccess(user, true)}
                >
                    Revoke
                </Menu.Item>
                <Menu.Item
                    key="delete"
                    danger
                    onClick={() => handleDeleteUser(user)}
                >
                    Delete
                </Menu.Item>
            </Menu>
        )
    }

    // [NEW] Handle Form Submission
    const handleInviteUser = async (values: IInviteUser) => {
        setIsSubmitting(true)
        try {
            await fetchInviteUser(values)
            message.success('User invite sent successfully')
            setIsModalVisible(false)
            form.resetFields()
            fetchAllUsers() // Refresh the list
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to invite user'
            message.error(errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    // [NEW] Handle Modal Cancel
    const handleCancel = () => {
        setIsModalVisible(false)
        form.resetFields()
    }

    const columns = [
        {
            title: 'User',
            dataIndex: 'UserInfo',
            key: 'UserInfo',
            render: (checked: any, item: any) => (
                <UserInfoCell>
                    <Avatar src={item.logo} style={{ marginRight: '.3rem' }}>
                        {getName(item)[0]}
                    </Avatar>
                    <div>
                        <h4 style={{ margin: '0px' }}>{getName(item)}</h4>
                        <div style={{ fontSize: '12px', color: '#8f8f8f' }}>
                            {item.email}
                        </div>
                    </div>
                </UserInfoCell>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'subscription',
            key: 'subscription',
            render: (value: string, item: any) => {
                if (item.isAdmin) return 'Admin'
                return value || SUBSCRIPTION.FREE
            },
        },
        {
            title: 'Registered',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value: any) => {
                const date = moment(value * 1000)
                return (
                    <div>
                        <div>{date.format('MMMM DD, YYYY')}</div>
                        <div style={{ fontSize: '12px', color: '#8f8f8f' }}>
                            {date.format('dddd')}
                        </div>
                    </div>
                )
            },
        },
        {
            title: '# of Projects',
            dataIndex: 'forms',
            key: 'forms',
            render: (value: any) => {
                return value.length
            },
        },
        {
            title: 'Revenue',
            dataIndex: 'Revenue',
            key: 'Revenue',
            render: (value: string) => {
                return '$0.00'
            },
        },
        {
            title: 'Status',
            dataIndex: 'disabled',
            key: 'disabled',
            render: (value: boolean) => {
                return value ? 'Revoked' : 'Active'
            },
        },
        {
            title: '',
            dataIndex: 'Action',
            key: 'Action',
            render: (checked: any, item: any) => (
                <Dropdown overlay={actionMenu(item)} placement="bottomCenter">
                    <MoreOutlined />
                </Dropdown>
            ),
        },
    ]

    const renderPageButtons = () => {
        const retArr = []
        const startPage = 1
        const endPage: number = Math.ceil(totalSize / pageSize)

        for (let i = startPage; i <= endPage; i++) {
            retArr.push(
                <Button
                    type="primary"
                    shape="circle"
                    onClick={(e) => setCurrentPage(i)}
                    className={i === currentPage ? 'selected' : ''}
                >
                    {i}
                </Button>
            )
        }
        return retArr
    }

    const renderPageSizeSelect = () => {
        const retArr = []
        for (let i = 0; i < pageOptions.length; i++) {
            retArr.push(
                <Menu.Item onClick={(e) => setPageSize(pageOptions[i])}>
                    {pageOptions[i]}
                </Menu.Item>
            )
        }
        return <Menu>{retArr}</Menu>
    }

    const getDescription = () => {
        return (
            <>
                Showing{' '}
                <span className="hightlight">
                    {pageSize * (currentPage - 1) + 1} -{' '}
                    {Math.min(pageSize * currentPage, totalSize)}
                </span>{' '}
                of {totalSize} records
            </>
        )
    }

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const nextPage = () => {
        const maxPage = Math.ceil(totalSize / pageSize)
        if (currentPage < maxPage) {
            setCurrentPage(currentPage + 1)
        }
    }

    return (
        <Spin spinning={isLoading(requestStatus)}>
            <Container>
                <Main>
                    <div className="header-row">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsModalVisible(true)}
                        >
                            Add User
                        </Button>
                    </div>

                    <div className="userList">
                        <Table
                            dataSource={showingData}
                            columns={columns}
                            pagination={false}
                        />
                        <PaginationBar>
                            <div className="description">
                                {getDescription()}
                            </div>
                            <div className="page-buttons">
                                {renderPageButtons()}
                            </div>
                            <div className="page-toolbar">
                                <div className="page-size-select">
                                    <Dropdown
                                        overlay={renderPageSizeSelect()}
                                        placement="bottomCenter"
                                    >
                                        <Space>
                                            Show {pageSize} rows{' '}
                                            <CaretDownOutlined />
                                        </Space>
                                    </Dropdown>
                                </div>
                                <div className="page-action-buttons">
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<LeftOutlined />}
                                        onClick={prevPage}
                                    />
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<RightOutlined />}
                                        onClick={nextPage}
                                    />
                                </div>
                            </div>
                        </PaginationBar>
                    </div>
                    {/* [NEW] Add User Modal */}
                    <Modal
                        title="Add New User"
                        visible={isModalVisible}
                        onOk={() => form.submit()}
                        onCancel={handleCancel}
                        confirmLoading={isSubmitting}
                        okText="Send Invite"
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleInviteUser}
                        >
                            <Form.Item
                                name="firstName"
                                label="First Name"
                                rules={[
                                    ...getRegularRules('First Name'),
                                    {
                                        max: 50,
                                        message:
                                            'First name cannot exceed 50 characters',
                                    },
                                ]}
                            >
                                <Input placeholder="Enter first name" />
                            </Form.Item>
                            <Form.Item
                                name="lastName"
                                label="Last Name"
                                rules={[
                                    ...getRegularRules('Last Name'),
                                    {
                                        max: 50,
                                        message:
                                            'Last name cannot exceed 50 characters',
                                    },
                                ]}
                            >
                                <Input placeholder="Enter last name" />
                            </Form.Item>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    ...getRegularRules('Email'),
                                    {
                                        type: 'email',
                                        message: 'Please enter a valid email',
                                    },
                                ]}
                            >
                                <Input placeholder="Enter email address" />
                            </Form.Item>
                        </Form>
                    </Modal>

                    <Modal
                        title="Edit User Details"
                        visible={isEditModalVisible}
                        onOk={() => editForm.submit()}
                        onCancel={() => {
                            setIsEditModalVisible(false)
                            setEditingUser(null)
                            editForm.resetFields()
                        }}
                        confirmLoading={isSubmitting}
                        okText="Save Changes"
                    >
                        <Form
                            form={editForm}
                            layout="vertical"
                            onFinish={handleUpdateUser}
                        >
                            <Form.Item
                                name="firstName"
                                label="First Name"
                                rules={[
                                    ...getRegularRules('First Name'),
                                    {
                                        max: 50,
                                        message:
                                            'First name cannot exceed 50 characters',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="lastName"
                                label="Last Name"
                                rules={[
                                    ...getRegularRules('Last Name'),
                                    {
                                        max: 50,
                                        message:
                                            'Last name cannot exceed 50 characters',
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="isAdmin"
                                label="Grant Admin Access"
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                    disabled={
                                        editingUser?.id ===
                                        Number(
                                            localStorage.getItem(
                                                STORAGE_KEYS.ID
                                            )
                                        )
                                    }
                                />
                            </Form.Item>
                        </Form>
                    </Modal>
                </Main>
            </Container>
        </Spin>
    )
}

export default Users
